import React from 'react';
import { motion } from 'framer-motion';
import { Car, Bell, Wallet, Shield, Clock, CheckCircle, AlertTriangle, ChevronRight, Star, Scan, Zap, TrendingUp, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { VehicleBuddyLogo } from '../components/VehicleBuddyLogo';
import Footer from '../components/Footer';
import CountUp from 'react-countup';

const EnhancedLandingPage = () => {
  // High-quality hero background images (using highest quality from Unsplash)
  const heroImages = [
    'https://images.unsplash.com/photo-1709125559856-f2369819a1d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBtb2Rlcm4lMjBjYXIlMjBpbnRlcmlvciUyMGRhc2hib2FyZCUyMHZpZXclMjBkcml2aW5nfGVufDB8fHx8MTc3NTY0NjgwOXww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1692213080768-c6ed304b483f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxzY2VuaWMlMjByb2FkJTIwaGlnaHdheSUyMGRyaXZpbmclMjBVSyUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzU2NDY4MDl8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1760689044995-bf161137e058?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjYXIlMjBkYXNoYm9hcmQlMjBjb2NrcGl0JTIwbW9kZXJuJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzU2NDY4MDl8MA&ixlib=rb-4.1.0&q=85'
  ];

  // Use the best image - modern car dashboard with beautiful lighting
  const heroBg = heroImages[0];

  const features = [
    {
      icon: Scan,
      title: 'AI Document Scanner',
      description: 'Snap photos of your V5C, insurance, MOT cert, or receipts. AI instantly extracts all details - no typing!',
      highlight: true,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Car,
      title: 'Multi-Vehicle Management',
      description: 'Manage unlimited vehicles in one place. Add via UK registration plate or scan your V5C document.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Bell,
      title: 'Smart Reminders',
      description: 'Never miss MOT, tax, or insurance deadlines. Get email and SMS notifications 30 days in advance.',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Wallet,
      title: 'Expense Tracking',
      description: 'Track fuel, repairs, insurance, and running costs. Perfect for tax returns and budgeting.',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: AlertTriangle,
      title: 'Accident Evidence Kit',
      description: 'Guided photo capture, video recording, and witness details. Generate insurance-ready PDF reports.',
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Clock,
      title: 'Maintenance History',
      description: 'Digital service book with full repair history. Increases resale value when selling your vehicle.',
      gradient: 'from-indigo-500 to-purple-500'
    }
  ];

  const stats = [
    { number: 5000, suffix: '+', label: 'Happy Drivers' },
    { number: 12000, suffix: '+', label: 'Vehicles Managed' },
    { number: 99, suffix: '%', label: 'Accuracy Rate' },
    { number: 24, suffix: '/7', label: 'Available' }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Sign Up Free',
      description: 'Create your account in seconds with email',
      icon: '🚀'
    },
    {
      step: 2,
      title: 'Scan Your Documents',
      description: 'Use AI to extract vehicle details from V5C',
      icon: '📸'
    },
    {
      step: 3,
      title: 'Track Everything',
      description: 'Manage costs, reminders, and maintenance',
      icon: '📊'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <motion.header 
        className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          <VehicleBuddyLogo showText={true} className="h-7" />
          <div className="flex items-center gap-4">
            <Button variant="ghost" asChild className="hidden sm:inline-flex">
              <a href="#features">Features</a>
            </Button>
            <Button variant="ghost" asChild className="hidden sm:inline-flex">
              <a href="#how-it-works">How It Works</a>
            </Button>
            <Button asChild data-testid="nav-login-button" className="btn-glow">
              <Link to="/login">Sign In</Link>
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Hero Section with Background Image */}
      <section 
        className="relative py-20 sm:py-32 min-h-[90vh] flex items-center"
        style={{ 
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-cyan-900/85 z-0" />
        
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 via-transparent to-accent/30 z-0" />
        
        {/* Content */}
        <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col items-center text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <motion.div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400/20 backdrop-blur-sm border border-cyan-300/30 mb-8"
                whileHover={{ scale: 1.05 }}
              >
                <Zap className="h-4 w-4 text-cyan-300" />
                <span className="text-sm font-medium text-cyan-100">Powered by Us - Driven by You</span>
              </motion.div>

              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-6 text-white drop-shadow-lg">
                Your Complete{' '}
                <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text text-transparent inline-block">
                  Vehicle Ownership
                </span>
                {' '}Companion
              </h1>

              <p className="text-xl sm:text-2xl text-cyan-50 mb-12 leading-relaxed max-w-2xl mx-auto drop-shadow">
                Never miss a deadline, track every penny, and keep all your vehicle documents in one secure place. Built for UK drivers.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="text-lg h-14 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/60 transition-all"
                  data-testid="hero-get-started"
                >
                  <Link to="/login">
                    Get Started Free
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline" 
                  size="lg"
                  className="text-lg h-14 px-8 bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 hover:border-white/50"
                >
                  <a href="#features">
                    See Features
                  </a>
                </Button>
              </div>
            </motion.div>

            {/* Floating Stats */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 w-full max-w-4xl"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center border border-white/20 shadow-xl card-hover"
                  whileHover={{ y: -5, scale: 1.05 }}
                >
                  <div className="text-4xl font-bold bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent mb-2 counter-number">
                    <CountUp 
                      end={stat.number} 
                      duration={2.5} 
                      separator="," 
                      suffix={stat.suffix}
                    />
                  </div>
                  <div className="text-sm text-cyan-100 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 sm:py-32 bg-slate-900">
        <div className="container px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              Everything You Need,{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                In One Place
              </span>
            </h2>
            <p className="text-xl text-cyan-100/80 max-w-2xl mx-auto">
              Stop juggling multiple apps and paper documents. VehicleBuddy is your all-in-one solution.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card 
                    className={`card-hover h-full bg-slate-800/50 backdrop-blur border-slate-700/50 ${
                      feature.highlight ? 'ring-2 ring-cyan-400/50 bg-gradient-to-br from-cyan-900/30 to-blue-900/30' : ''
                    }`}
                  >
                    <CardContent className="pt-6 p-6">
                      {feature.highlight && (
                        <motion.div 
                          className="mb-4"
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ type: "spring", delay: 0.2 }}
                        >
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-bold shadow-lg">
                            <Star className="h-3 w-3" fill="currentColor" />
                            AI-Powered
                          </span>
                        </motion.div>
                      )}
                      
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient || 'from-cyan-500 to-blue-600'} flex items-center justify-center mb-4 shadow-lg float`}>
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                      <p className="text-cyan-100/70 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* App Preview Section - NEW */}
      <section className="py-20 sm:py-32 bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-500 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/30 rounded-full blur-3xl" />
        </div>
        <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-white drop-shadow-lg">
                Manage Everything{' '}
                <span className="text-yellow-300">From Your Phone</span>
              </h2>
              <p className="text-xl text-white/90 mb-8">
                VehicleBuddy works seamlessly on mobile and desktop. Scan documents, track expenses, 
                and get reminders - all while on the go.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Instant Document Scanning</h4>
                    <p className="text-sm text-white/80">
                      Take a photo, AI extracts everything. No typing required.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Automatic Reminders</h4>
                    <p className="text-sm text-white/80">
                      Never miss MOT, tax, or insurance deadlines again.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Secure Cloud Storage</h4>
                    <p className="text-sm text-white/80">
                      All your documents backed up and accessible anywhere.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1758411898471-fa144c487c85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxzbWFydHBob25lJTIwYXBwJTIwdmVoaWNsZSUyMG1hbmFnZW1lbnQlMjBtb2Rlcm58ZW58MHx8fHwxNzc1NjQ2ODA5fDA&ixlib=rb-4.1.0&q=85"
                  alt="VehicleBuddy mobile app"
                  className="rounded-3xl shadow-2xl w-full max-w-md mx-auto ring-4 ring-white/30"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-yellow-400/30 rounded-full blur-3xl" />
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-pink-400/30 rounded-full blur-3xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 sm:py-32 bg-slate-900">
        <div className="container px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              Get Started in{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                3 Simple Steps
              </span>
            </h2>
            <p className="text-xl text-cyan-100/70">
              Start managing your vehicles like a pro in under 2 minutes
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="relative"
                >
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-1/4 left-full w-full h-0.5 bg-gradient-to-r from-cyan-500 to-transparent -translate-x-1/2 z-0" />
                  )}
                  
                  <Card className="relative card-hover text-center p-8 h-full bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur border-slate-700/50">
                    <div className="text-6xl mb-4">{item.icon}</div>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold text-lg mb-4 shadow-lg">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-white">{item.title}</h3>
                    <p className="text-cyan-100/70">{item.description}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-accent relative overflow-hidden">
        <div className="absolute inset-0 bg-animated-gradient opacity-50" />
        <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="max-w-3xl mx-auto text-center text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              Ready to Take Control of Your Vehicles?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Join thousands of UK drivers who never miss a deadline or lose a receipt.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                asChild 
                size="lg" 
                variant="secondary"
                className="text-lg h-14 px-8 bg-white text-primary hover:bg-white/90 btn-glow"
              >
                <Link to="/login">
                  Start Free Today
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <p className="mt-6 text-sm text-white/70">
              No credit card required • Free forever • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default EnhancedLandingPage;